import React, { createContext, useContext, useEffect, useState } from 'react';
import { getConsent, onConsentChange, purgeAnalyticsArtifacts } from './consentStore';
import { syncClarityConsent } from '../clarityAnalytics';
import { syncFirebaseAnalyticsConsent } from '../firebaseAnalytics';
import CookieBanner from './CookieBanner';

const ConsentContext = createContext(null);

export function ConsentProvider({ children }) {
  const [consent, setConsentState] = useState(() => getConsent());
  const [isBannerOpen, setIsBannerOpen] = useState(false);

  useEffect(() => {
    syncClarityConsent(consent);
    void syncFirebaseAnalyticsConsent(consent);

    // Listen for changes emitted by the consent store
    const unsubscribe = onConsentChange(async (newConsent) => {
      setConsentState(newConsent);
      // Close the banner after a choice is made
      setIsBannerOpen(false);

      const hadTrackingCookies = document.cookie
        .split(';')
        .some((cookie) => /^\s*(?:_ga|_gid|_clck|_clsk)=/.test(cookie));

      syncClarityConsent(newConsent);
      await syncFirebaseAnalyticsConsent(newConsent);

      // If they rejected analytics, purge them. 
      // A reload stops already-running analytics scripts after consent withdrawal.
      if (newConsent && newConsent.analytics === false) {
        await purgeAnalyticsArtifacts();

        if (hadTrackingCookies) {
          window.location.reload();
        }
      }
    });

    return unsubscribe;
  }, []);

  return (
    <ConsentContext.Provider value={{ consent, setIsBannerOpen }}>
      {children}
      {/* If consent is null, the user must choose. Or if they explicitly opened it */}
      {(consent === null || isBannerOpen) && (
        <CookieBanner 
          onDismiss={() => setIsBannerOpen(false)} 
          showDismiss={consent !== null} 
        />
      )}
      
      {/* If consent is NOT null and banner is NOT open, show the tiny floating button */}
      {(consent !== null && !isBannerOpen) && (
        <button 
          className="pc-floating-btn" 
          onClick={() => setIsBannerOpen(true)}
          aria-label="Manage Cookie Settings"
          title="Manage Cookie Settings"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
            <path d="M8.5 8.5v.01"/>
            <path d="M16 12.5v.01"/>
            <path d="M12 16v.01"/>
            <path d="M11 11.5v.01"/>
          </svg>
        </button>
      )}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  return useContext(ConsentContext);
}

export function CookieSettingsLink({ className, children }) {
  const { setIsBannerOpen } = useConsent();

  return (
    <a 
      href="#"
      className={className} 
      onClick={(e) => {
        e.preventDefault();
        setIsBannerOpen(true);
      }}
    >
      {children || 'Cookie settings'}
    </a>
  );
}
